/**
 * CLOSET by HAJI - Custom Stock Status Handler v2.1
 * バリアント選択時に【取寄せ】を検出し、CTAボタンと通知を動的に制御
 *
 * v2.1 (2026-04-22): getCurrentVariantId がページ内の別フォーム(Sticky CTA 等)の
 *   input[name="id"] を拾って初期値固定になるバグを修正。
 *   チェック中の option ラジオから variant JSON を逆引きする方式に変更。
 */

(function() {
  'use strict';

  // ============================
  // 設定
  // ============================
  const CONFIG = {
    // 検出パターン
    patterns: {
      backorder: '【取寄せ】',
      inStock: '【在庫有】'
    },
    
    // 表示テキスト
    messages: {
      addToCart: 'カートに追加する',
      backorderCart: '取寄せ品をカートに追加',
      soldOut: '売り切れ'
    },
    
    // セレクタ
    selectors: {
      addToCartBtn: '#AddToCart',
      backorderNotice: '#backorder-notice',
      variantDataScript: '#variant-stock-info',
      variantInput: 'input[name="id"]',
      productForm: '.product-form',
      blockSwatchRadio: '.block-swatch__radio',
      colorSwatchRadio: '.color-swatch__radio',
      variantSwatchRadio: '.variant-swatch__radio'
    }
  };

  // ============================
  // 状態管理
  // ============================
  let variantStockData = null;
  let isInitialized = false;

  // ============================
  // ユーティリティ関数
  // ============================

  /**
   * バリアント在庫データを取得（キャッシュ付き）
   */
  function getVariantStockData() {
    if (variantStockData) {
      return variantStockData;
    }
    
    const script = document.querySelector(CONFIG.selectors.variantDataScript);
    if (!script) {
      console.warn('CLOSET: variant-stock-type-data not found');
      return {};
    }
    
    try {
      variantStockData = JSON.parse(script.textContent);
      return variantStockData;
    } catch (e) {
      console.error('CLOSET: Failed to parse variant stock data:', e);
      return {};
    }
  }

  /**
   * 現在選択中のバリアントIDを取得
   *
   * 実装メモ:
   * ページ内に input[name="id"] が複数存在（Sticky CTA / installment / main form）し、
   * 先頭を拾うと常に初期値のままになってしまう。また Focal の <product-variants> は
   * masterSelector の代入に型バグがあり variant:changed イベントの挙動も信頼できない。
   * そのため、チェック中の option1/option2/option3 ラジオから variant JSON を逆引きする。
   */
  function getCurrentVariantId() {
    // 最優先: チェック中のオプション値 → variant JSON の option1/2/3 を突合
    const stockData = getVariantStockData();
    const variants = (stockData && stockData.variants) || [];
    if (variants.length) {
      const scope = document.querySelector('product-variants, .product-form__variants') || document;
      const optionEls = Array.prototype.slice.call(
        scope.querySelectorAll('input[name^="option"]:checked, select[name^="option"]')
      );
      // name="option1","option2",... の順に整列（DOM順に頼らない）
      optionEls.sort(function(a, b) { return (a.name || '').localeCompare(b.name || ''); });
      const checked = optionEls.map(function(el) { return el.value; });
      if (checked.length) {
        for (let i = 0; i < variants.length; i++) {
          const v = variants[i];
          const opts = [v.option1, v.option2, v.option3].filter(function(o) {
            return o !== null && o !== undefined;
          });
          if (opts.length === checked.length && opts.every(function(o, idx) { return o === checked[idx]; })) {
            return String(v.id);
          }
        }
      }
    }

    // フォールバック1: メインの product-form 内の有効な id input / select
    const mainIdEl = document.querySelector(
      'form[is="product-form"] input[name="id"]:not([disabled]), form[is="product-form"] select[name="id"]'
    );
    if (mainIdEl && mainIdEl.value) return mainIdEl.value;

    // フォールバック2: product-form スコープの input[name="id"]（disabled含む、初期値）
    const anyMainIdEl = document.querySelector('form[is="product-form"] input[name="id"]');
    if (anyMainIdEl && anyMainIdEl.value) return anyMainIdEl.value;

    // 最終フォールバック: 旧実装
    const variantInput = document.querySelector(CONFIG.selectors.variantInput);
    if (variantInput && variantInput.value) return variantInput.value;

    return null;
  }

  /**
   * 現在選択中のバリアントが取寄せかどうか判定
   */
  function isCurrentVariantBackorder() {
    const stockData = getVariantStockData();
    const variantId = getCurrentVariantId();
    const variants = (stockData && stockData.variants) || [];
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === String(variantId)) {
        return variants[i].is_backorder === true;
      }
    }
    return checkBackorderFromSelectedOptions();
  }

  /**
   * 選択中のオプションから取寄せかどうかを直接判定（フォールバック）
   */
  function checkBackorderFromSelectedOptions() {
    // チェックされているサイズボタンを取得
    const checkedRadio = document.querySelector('.block-swatch__radio:checked');
    if (checkedRadio) {
      const stockType = checkedRadio.getAttribute('data-stock-type');
      return stockType === 'backorder';
    }
    return false;
  }

  // ============================
  // UI更新関数
  // ============================

  /**
   * 現在のバリアントが売り切れ（在庫有で在庫0）かどうか判定
   */
  function isCurrentVariantSoldOut() {
    const addToCartBtn = document.querySelector(CONFIG.selectors.addToCartBtn);
    return addToCartBtn && addToCartBtn.disabled;
  }

  /**
   * Prime Review お気に入り（通知）ボタンの表示制御
   * 全バリアントで表示し、状態に応じてテキストとカラーを変更
   */
  function updateFavoritesButton() {
    const favBtn = document.querySelector('.mxpr-favorites-button');
    if (!favBtn) return;

    const isBackorder = isCurrentVariantBackorder();
    const isSoldOut = isCurrentVariantSoldOut();

    // 常に表示
    favBtn.style.display = '';

    if (isBackorder || isSoldOut) {
      // 取寄せ or 売り切れ → 再入荷通知（ダーク系・落ち着いた印象）
      favBtn.textContent = '🔔 再入荷通知を受け取る';
      favBtn.setAttribute('data-favbtn-state', 'restock');
      console.log('CLOSET: Restock notification button (backorder:', isBackorder, ', soldOut:', isSoldOut, ')');
    } else {
      // 在庫あり → 値下がり通知（モノトーン・視認性優先）
      favBtn.textContent = '🏷️ お得な値下げ通知を受け取る';
      favBtn.setAttribute('data-favbtn-state', 'in-stock');
      console.log('CLOSET: Price drop notification button (in stock)');
    }

    // 注釈テキストの追加
    let noteEl = document.getElementById('mxpr-line-note');
    if (!noteEl) {
      noteEl = document.createElement('p');
      noteEl.id = 'mxpr-line-note';
      noteEl.style.cssText = 'font-size:10px; color:#999; text-align:left; margin:4px 0 0; letter-spacing:0.03em;';
      // ボタンと同じ幅内で中央寄せ
      var btnWidth = favBtn.offsetWidth;
      if (btnWidth > 0) {
        noteEl.style.width = btnWidth + 'px';
        noteEl.style.textAlign = 'center';
      }
      noteEl.textContent = '※ LINE連携が必要です';
      favBtn.parentNode.insertBefore(noteEl, favBtn.nextSibling);
    }
  }

  // ============================
  // 営業日＆最短到着日計算
  // ============================
  // 営業日: 木(4) 金(5) 土(6) 日(0) 月(1) / 休: 火(2) 水(3)
  // カットオフ: 12:00
  // 在庫あり: 発送翌日着
  // 取寄せ: 注文日から最短7日後

  function isBusinessDay(d) {
    var day = d.getDay();
    return day !== 2 && day !== 3;
  }

  function nextBusinessDay(d) {
    var nd = new Date(d);
    nd.setDate(nd.getDate() + 1);
    while (!isBusinessDay(nd)) nd.setDate(nd.getDate() + 1);
    return nd;
  }

  function getShippingDay() {
    var now = new Date();
    if (isBusinessDay(now) && now.getHours() < 12) return now;
    return nextBusinessDay(now);
  }

  function formatJpDate(d) {
    var dayNames = ['日', '月', '火', '水', '木', '金', '土'];
    return (d.getMonth() + 1) + '/' + d.getDate() + '（' + dayNames[d.getDay()] + '）';
  }

  function calcEtaText(isBackorder) {
    if (isBackorder) {
      var bo = new Date();
      bo.setDate(bo.getDate() + 7);
      return '最短 ' + formatJpDate(bo) + ' 頃到着予想';
    }
    var shipDay = getShippingDay();
    var arriveDay = new Date(shipDay);
    arriveDay.setDate(arriveDay.getDate() + 1);
    return '最短 ' + formatJpDate(arriveDay) + ' 到着';
  }

  function updateClosetStockInfo() {
    var wrap = document.querySelector('[data-closet-stock-info]');
    if (!wrap) return;
    var statusEl = wrap.querySelector('[data-closet-status]');
    var etaEl = wrap.querySelector('[data-closet-eta]');
    if (!statusEl || !etaEl) return;

    // variant JSON から is_backorder / available / inventory_quantity を取得（最も信頼できる）
    var stockData = getVariantStockData();
    var variantId = getCurrentVariantId();
    var variants = (stockData && stockData.variants) || [];
    var variant = null;
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === String(variantId)) { variant = variants[i]; break; }
    }

    var isBackorder = variant ? variant.is_backorder === true : false;
    var isAvailable = variant ? variant.available === true : true;
    var qty = variant ? (variant.inventory_quantity || 0) : 0;

    // variantが見つからない場合：ラジオ値から backorder 判定、qty は不明（在庫あり扱い）
    if (!variant) {
      var checkedRadios = document.querySelectorAll('input[type="radio"]:checked');
      for (var r = 0; r < checkedRadios.length; r++) {
        var rv = checkedRadios[r].value || '';
        if (rv.indexOf('取寄せ') !== -1) { isBackorder = true; break; }
      }
      // variantが不明の時の在庫数は0にせず-1 扱い（qty === 1判定を避ける）
      qty = -1;
    }

    // ATC ボタンが disabled なら売り切れ扱い
    var soldOut = isCurrentVariantSoldOut();

    console.log('CLOSET stock info:', { variantId: variantId, found: !!variant, isBackorder: isBackorder, isAvailable: isAvailable, soldOut: soldOut, qty: qty });

    // 購入不可（売り切れ）なら非表示
    if (!isAvailable || soldOut) {
      wrap.style.display = 'none';
      return;
    }

    statusEl.classList.remove('is-blue', 'is-red');
    if (isBackorder) {
      statusEl.textContent = 'お取り寄せ可能';
      statusEl.classList.add('is-blue');
    } else if (qty === 1) {
      statusEl.textContent = '残り在庫数1';
      statusEl.classList.add('is-red');
    } else {
      // qty >= 2 または qty 不明 → 在庫あり
      statusEl.textContent = '在庫あり';
      statusEl.classList.add('is-blue');
    }
    wrap.style.display = '';
    etaEl.textContent = calcEtaText(isBackorder);
  }

  /**
   * UIを更新（通知バナーとCTAボタン）
   */
  function updateUI() {
    const isBackorder = isCurrentVariantBackorder();
    const addToCartBtn = document.querySelector(CONFIG.selectors.addToCartBtn);
    const backorderNotice = document.querySelector(CONFIG.selectors.backorderNotice);

    console.log('CLOSET: updateUI called, isBackorder:', isBackorder);

    if (!addToCartBtn) {
      console.warn('CLOSET: AddToCart button not found');
      return;
    }

    // ボタンが無効状態（売り切れ）の場合
    if (addToCartBtn.disabled) {
      if (backorderNotice) {
        backorderNotice.style.display = 'none';
      }
      addToCartBtn.classList.remove('button--backorder');
      // 売り切れ時は再入荷通知ボタンを表示＋在庫情報表示も更新（非表示処理）
      updateFavoritesButton();
      updateClosetStockInfo();
      return;
    }

    if (isBackorder) {
      // === 取寄せ品の場合 ===
      console.log('CLOSET: Setting backorder state');

      // 通知バナーを表示
      if (backorderNotice) {
        backorderNotice.style.display = 'flex';
      }

      // ボタンにクラスを追加
      addToCartBtn.classList.add('button--backorder');

      // ボタンテキストを変更
      updateButtonText(addToCartBtn, CONFIG.messages.backorderCart);

    } else {
      // === 通常在庫の場合 ===
      console.log('CLOSET: Setting in-stock state');

      // 通知バナーを非表示
      if (backorderNotice) {
        backorderNotice.style.display = 'none';
      }

      // ボタンからクラスを削除
      addToCartBtn.classList.remove('button--backorder');

      // ボタンテキストを元に戻す
      updateButtonText(addToCartBtn, CONFIG.messages.addToCart);
    }

    // Prime Review ボタンの表示制御
    updateFavoritesButton();

    // CLOSET カスタム在庫＋到着日表示を更新
    updateClosetStockInfo();
  }

  /**
   * ボタンテキストを更新
   */
  function updateButtonText(button, newText) {
    // Focalテーマのloader-buttonは内部にテキストを持つ
    // 直接テキストノードを探して更新
    const childNodes = button.childNodes;
    
    for (let i = 0; i < childNodes.length; i++) {
      const node = childNodes[i];
      // テキストノードで、空白以外の内容がある場合
      if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
        node.textContent = newText;
        return;
      }
    }
    
    // テキストノードが見つからない場合、span要素を探す
    const textSpan = button.querySelector('span:not(.visually-hidden):not(.loader)');
    if (textSpan) {
      textSpan.textContent = newText;
      return;
    }
    
    // それでも見つからない場合、最初のテキストを置換
    if (button.firstChild && button.firstChild.nodeType === Node.TEXT_NODE) {
      button.firstChild.textContent = newText;
    }
  }

  // ============================
  // イベントリスナー
  // ============================

  /**
   * イベントリスナーを設定
   */
  function setupEventListeners() {
    // 1. ブロックスウォッチ（サイズボタン）のクリック
    document.querySelectorAll(CONFIG.selectors.blockSwatchRadio).forEach(function(radio) {
      radio.addEventListener('change', function() {
        console.log('CLOSET: block-swatch changed');
        // 少し遅延させてバリアントIDの更新を待つ
        setTimeout(updateUI, 150);
      });
    });

    // 2. カラースウォッチのクリック
    document.querySelectorAll(CONFIG.selectors.colorSwatchRadio).forEach(function(radio) {
      radio.addEventListener('change', function() {
        console.log('CLOSET: color-swatch changed');
        setTimeout(updateUI, 150);
      });
    });

    // 3. バリアントスウォッチ（画像）のクリック
    document.querySelectorAll(CONFIG.selectors.variantSwatchRadio).forEach(function(radio) {
      radio.addEventListener('change', function() {
        console.log('CLOSET: variant-swatch changed');
        setTimeout(updateUI, 150);
      });
    });

    // 4. hidden input の value 変更を監視（MutationObserver）
    const variantInput = document.querySelector(CONFIG.selectors.variantInput);
    if (variantInput) {
      // MutationObserver for attribute changes
      const observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
            console.log('CLOSET: variant input value changed (attribute)');
            setTimeout(updateUI, 50);
          }
        });
      });
      
      observer.observe(variantInput, { 
        attributes: true,
        attributeFilter: ['value']
      });

      // Property descriptor override for programmatic changes
      const descriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
      if (descriptor && descriptor.set) {
        const originalSetter = descriptor.set;
        Object.defineProperty(variantInput, 'value', {
          get: descriptor.get,
          set: function(val) {
            originalSetter.call(this, val);
            console.log('CLOSET: variant input value changed (property)');
            setTimeout(updateUI, 50);
          },
          configurable: true
        });
      }
    }

    // 5. Shopifyのカスタムイベント
    document.addEventListener('variant:changed', function(e) {
      console.log('CLOSET: variant:changed event fired');
      setTimeout(updateUI, 50);
    });

    // 6. 全てのproduct-form内のinput変更を監視（包括的）
    const productForm = document.querySelector(CONFIG.selectors.productForm);
    if (productForm) {
      productForm.addEventListener('change', function(e) {
        if (e.target.type === 'radio' || e.target.tagName === 'SELECT') {
          console.log('CLOSET: product-form input changed');
          setTimeout(updateUI, 150);
        }
      });
    }

    // 7. クリックイベントでの補完（ラベルクリック対応）
    document.querySelectorAll('.block-swatch__item').forEach(function(label) {
      label.addEventListener('click', function() {
        console.log('CLOSET: block-swatch label clicked');
        setTimeout(updateUI, 200);
      });
    });
  }

  // ============================
  // 初期化
  // ============================

  /**
   * 初期化
   */
  function init() {
    // 既に初期化済みの場合はスキップ
    if (isInitialized) {
      console.log('CLOSET: Already initialized, skipping');
      return;
    }
    
    // 商品ページかどうかチェック
    if (!document.querySelector(CONFIG.selectors.productForm)) {
      console.log('CLOSET: Not a product page, skipping');
      return;
    }

    console.log('CLOSET: Initializing stock handler v2');
    
    // キャッシュをリセット
    variantStockData = null;
    
    // イベントリスナーを設定
    setupEventListeners();

    // 初期状態を更新
    updateUI();

    // アプリ側が favoritesボタンを再描画して data-favbtn-state が消えた場合に再適用
    const favBtnInit = document.querySelector('.mxpr-favorites-button');
    if (favBtnInit && favBtnInit.parentNode) {
      const reobserver = new MutationObserver(function() {
        const btn = document.querySelector('.mxpr-favorites-button');
        if (btn && !btn.hasAttribute('data-favbtn-state')) {
          updateFavoritesButton();
        }
      });
      reobserver.observe(favBtnInit.parentNode, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    }

    isInitialized = true;
    console.log('CLOSET: Stock handler v2 initialized successfully');
  }

  /**
   * リセットして再初期化
   */
  function reinit() {
    isInitialized = false;
    variantStockData = null;
    init();
  }

  // ============================
  // 実行
  // ============================
  
  // DOM読み込み完了後に実行
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopifyのセクション再描画にも対応
  document.addEventListener('shopify:section:load', function() {
    console.log('CLOSET: Section reloaded, reinitializing');
    setTimeout(reinit, 100);
  });

  // ページ内遷移（Turbolinks/Swup等）対応
  document.addEventListener('turbolinks:load', reinit);
  document.addEventListener('swup:contentReplaced', reinit);

})();

/* ============================================
   Nubian風：コレクションセクションの「すべて見る」ボタンを
   section__header 内にクローン配置（PC版のみ、1001px以上）
   マルチブロック時はアクティブタブのコレクションURLに動的追従
   ============================================ */
(function() {
  var MQ = '(min-width: 1001px)';
  var SECTION_SELECTOR = '.shopify-section';
  var TARGETS = '.featured-collections, .product-recommendations, recently-viewed-products';
  var CLONE_CLASS = 'section__footer--nubian-clone';
  var BUTTON_LABEL = 'すべて見る';

  function getActiveProductList(section) {
    // 優先: aria-expanded="true" のタブが制御する product-list
    var activeTab = section.querySelector('.tabs-nav__item[aria-expanded="true"]');
    if (activeTab) {
      var id = activeTab.getAttribute('aria-controls');
      if (id) {
        var el = section.querySelector('#' + CSS.escape(id)) || document.getElementById(id);
        if (el) return el;
      }
    }
    // フォールバック: hidden なし product-list
    var lists = section.querySelectorAll('product-list');
    for (var i = 0; i < lists.length; i++) {
      if (!lists[i].hasAttribute('hidden')) return lists[i];
    }
    return lists[0] || null;
  }

  function updateHeaderFooter(section) {
    var header = section.querySelector('.section__header');
    if (!header) return;
    header.classList.add('section__header--nubian');

    var activeList = getActiveProductList(section);
    if (!activeList) return;
    var originalFooter = activeList.querySelector('.section__footer');
    if (!originalFooter) return;
    var originalLink = originalFooter.querySelector('a.button, a');
    if (!originalLink) return;

    var targetHref = originalLink.getAttribute('href') || '#';
    var existingClone = header.querySelector('.' + CLONE_CLASS);
    if (existingClone) {
      var existingLink = existingClone.querySelector('a');
      if (existingLink) {
        existingLink.setAttribute('href', targetHref);
        existingLink.textContent = BUTTON_LABEL;
      }
      return;
    }

    // 新規作成（テキストは "すべて見る" 固定、URLはアクティブコレクション）
    var wrap = document.createElement('div');
    wrap.className = 'section__footer ' + CLONE_CLASS;
    var a = document.createElement('a');
    a.className = 'button button--primary';
    a.setAttribute('href', targetHref);
    a.textContent = BUTTON_LABEL;
    wrap.appendChild(a);
    header.appendChild(wrap);
  }

  function setupSection(section) {
    updateHeaderFooter(section);
    // タブクリックで即座に更新（aria-expanded をポーリングして確実に反映）
    section.querySelectorAll('.tabs-nav__item').forEach(function(tab) {
      if (tab.__nubianBound) return;
      tab.__nubianBound = true;
      tab.addEventListener('click', function() {
        // requestAnimationFrame 2回で確実に aria-expanded 反映後に処理
        requestAnimationFrame(function() {
          requestAnimationFrame(function() { updateHeaderFooter(section); });
        });
      });
    });
    // aria-expanded の変化も監視（念のため）
    if (!section.__nubianObserver) {
      var obs = new MutationObserver(function() { updateHeaderFooter(section); });
      section.querySelectorAll('.tabs-nav__item').forEach(function(tab) {
        obs.observe(tab, { attributes: true, attributeFilter: ['aria-expanded'] });
      });
      section.__nubianObserver = obs;
    }
  }

  function cleanupSection(section) {
    var header = section.querySelector('.section__header');
    if (!header) return;
    var clone = header.querySelector('.' + CLONE_CLASS);
    if (clone) clone.remove();
    header.classList.remove('section__header--nubian');
  }

  function relocateAll() {
    var isPC = window.matchMedia(MQ).matches;
    document.querySelectorAll(SECTION_SELECTOR).forEach(function(section) {
      if (!section.querySelector(TARGETS)) return;
      if (isPC) {
        setupSection(section);
      } else {
        cleanupSection(section);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', relocateAll);
  } else {
    relocateAll();
  }
  var resizeTimer;
  window.addEventListener('resize', function() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(relocateAll, 150);
  });
  document.addEventListener('shopify:section:load', relocateAll);
})();

/* ============================================
   検索ドロワー：チップクリックで検索バーに入力
   ============================================ */
(function() {
  function bindChips() {
    var drawer = document.getElementById('search-drawer');
    if (!drawer) return;
    var input = drawer.querySelector('.predictive-search__input, input[name="q"]');
    if (!input) return;
    drawer.querySelectorAll('.predictive-search__popular-chip').forEach(function(chip) {
      if (chip.__bound) return;
      chip.__bound = true;
      chip.addEventListener('click', function() {
        var kw = chip.getAttribute('data-search-keyword') || chip.textContent.trim();
        input.value = kw;
        input.focus();
        // predictive-search の入力イベントを発火してサジェスト起動
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindChips);
  } else {
    bindChips();
  }
  document.addEventListener('shopify:section:load', bindChips);

  // ============================
  // Shopify Inbox: SPでフローティングCTAと重ならないよう位置を強制上書き
  // インラインstyleで bottom: 80px が指定されるため、JSで継続的に上書き
  // ============================
  (function() {
    const OFFSET_UP = 220;       // SP時の上方向オフセット(px)
    const MQ_SP = '(max-width: 1000px)';

    function applyOffset(el) {
      if (!el) return;
      const isSP = window.matchMedia(MQ_SP).matches;
      const isOpen = el.getAttribute('is-open') === 'true';
      if (isSP && !isOpen) {
        el.style.setProperty('bottom', OFFSET_UP + 'px', 'important');
      } else {
        el.style.removeProperty('bottom');
      }
    }

    function findChat() {
      return document.querySelector('inbox-online-store-chat, #ShopifyChat');
    }

    function observe() {
      const el = findChat();
      if (!el) return false;
      applyOffset(el);
      // 開閉状態の変化を監視
      const attrObs = new MutationObserver(() => applyOffset(el));
      attrObs.observe(el, { attributes: true, attributeFilter: ['is-open', 'style'] });
      // リサイズ対応
      window.addEventListener('resize', () => applyOffset(el));
      return true;
    }

    // Shopify Inboxは非同期挿入のため、要素出現を監視
    if (!observe()) {
      const bodyObs = new MutationObserver(() => {
        if (observe()) bodyObs.disconnect();
      });
      bodyObs.observe(document.documentElement, { childList: true, subtree: true });
      // 念のため30秒後に監視停止
      setTimeout(() => bodyObs.disconnect(), 30000);
    }
  })();
})();